import { useContext, useState, useRef } from "react";
import { MetaforceContext, WebEditorContext } from "../../contexts";
import * as Constants from "../../utils/constants"
import {useClassNames} from '../../hooks/useClassNames';
import { updateAttachment } from "../../api/webEditor";
import { ArrowLongDownIcon, ArrowLongUpIcon, TrashIcon } from "@heroicons/react/24/outline";
import AttachmentLibrary from "./attachmentLibrary";
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { resources, translations } from "../../translations/translationsConstants";

export default function AttachmentTable() {
    const { startParameter, toastHandler } = useContext(MetaforceContext);
    const { editorDisabled, editorFileRef, editorAttachment, setEditorAttachment, attachmentsToDeleteRef, autoSaveAttachment } = useContext(WebEditorContext);
    const { classNames } = useClassNames();
    const fileExtension = '.pdf';
    const maxFileSize = 1024000;
    const [libraryOpen, setLibraryOpen] = useState(false);
    const { t: ta } = useTranslation([resources.attachment]);
    const { t: terr } = useTranslation([resources.errors]);

    const sortAttachments = (clonedEditorAttachment) => {
        if (!clonedEditorAttachment) return;

        clonedEditorAttachment.Attachments = Array.isArray(clonedEditorAttachment.Attachments) ? clonedEditorAttachment.Attachments.sort(function (a, b) { return a.Order - b.Order }) : [];
    }

    const handleSave = (clonedEditorAttachment) => {
        sortAttachments(clonedEditorAttachment);

        setEditorAttachment(clonedEditorAttachment);

        autoSaveAttachment();
    }

    const handleAddFromLibrary = () => {
        setLibraryOpen(true);
    }

    const onLibraryClose = async (dxmlAttachments) => {
        setLibraryOpen(false);

        if (dxmlAttachments && dxmlAttachments.length > 0) {
            const clonedEditorAttachment = _.cloneDeep(editorAttachment);

            let maxOrder = editorAttachment.Attachments && editorAttachment.Attachments.length > 0 ? editorAttachment.Attachments[editorAttachment.Attachments.length - 1].Order + 1 : 0;
            dxmlAttachments.forEach((dxmlAttachment) => {
                dxmlAttachment.Uuid = uuidv4();
                dxmlAttachment.Action = Constants.attachmentActionType.createOrUpdate;
                dxmlAttachment.Order = maxOrder++;
                clonedEditorAttachment.Attachments.push(dxmlAttachment);
            })

            handleSave(clonedEditorAttachment);
        }
    }

    const handlePdfUpload = async (e) => {
        let file = e.target.files[0];
        if (file.size > maxFileSize) {
            toastHandler(terr(translations.errors.selectFileTooLarge), 'error');
            return;
        }

        if (checkFileExtension(file)) {
            readPdfAttachment(file)
        }
    };

    const readPdfAttachment = async (file) => {
        const result = await getFileContentAsBase64String(file);
        file["base64"] = result;

        const newDxmlAttachment = {
            Uuid: uuidv4(),
            Action: Constants.attachmentActionType.createOrUpdate,
            Name: file.name,
            Description: '',
            Base64PdfFile: result.split(',')[1],
            Order: editorAttachment.Attachments && editorAttachment.Attachments.length > 0 ? editorAttachment.Attachments[editorAttachment.Attachments.length - 1].Order + 1 : 0,
        };

        const clonedEditorAttachment = _.cloneDeep(editorAttachment);
        clonedEditorAttachment.Attachments.push(newDxmlAttachment);

        handleSave(clonedEditorAttachment);
    }

    const checkFileExtension = (file) => {
        const extension = file.name.split('.').pop().toLowerCase();
        const supportedExtention = fileExtension && (fileExtension[0] === '.' ? fileExtension.substring(1).toLowerCase() : fileExtension.toLowerCase());
        if (supportedExtention && extension !== supportedExtention) {
            toastHandler(`${terr(translations.errors.failLoadFileExtension)} '${extension}' ${terr(translations.errors.failLoadFileExtensionIsNotSupport)}`, 'error');
            return false;
        }
        return true;
    }

    const getFileContentAsBase64String = file => {
        return new Promise(resolve => {
            let content = "";
            let reader = new FileReader();

            reader.onload = () => {
                content = reader.result;
                resolve(content);
            };

            reader.readAsDataURL(file);
        });
    };

    const handleMoveDown = (dxmlAttachment) => {
        const index = editorAttachment.Attachments?.findIndex(x => x === dxmlAttachment);
        if (index >= 0 && index < editorAttachment.Attachments?.length - 1) {
            const clonedEditorAttachment = _.cloneDeep(editorAttachment);
            clonedEditorAttachment.Attachments[index].Order = clonedEditorAttachment.Attachments[index].Order + 1;
            clonedEditorAttachment.Attachments[index].Action = Constants.attachmentActionType.createOrUpdate;
            clonedEditorAttachment.Attachments[index + 1].Order = clonedEditorAttachment.Attachments[index + 1].Order - 1;
            clonedEditorAttachment.Attachments[index + 1].Action = Constants.attachmentActionType.createOrUpdate;

            handleSave(clonedEditorAttachment);
        }
    }

    const handleMoveUp = async (dxmlAttachment) => {
        const index = editorAttachment.Attachments?.findIndex(x => x === dxmlAttachment);
        if (index > 0) {
            const clonedEditorAttachment = _.cloneDeep(editorAttachment);
            clonedEditorAttachment.Attachments[index].Order = clonedEditorAttachment.Attachments[index].Order - 1;
            clonedEditorAttachment.Attachments[index].Action = Constants.attachmentActionType.createOrUpdate;
            clonedEditorAttachment.Attachments[index - 1].Order = clonedEditorAttachment.Attachments[index - 1].Order + 1;
            clonedEditorAttachment.Attachments[index - 1].Action = Constants.attachmentActionType.createOrUpdate;

            handleSave(clonedEditorAttachment);
        }
    }

    const handleDelete = async (dxmlAttachment) => {
        const index = editorAttachment.Attachments?.findIndex(x => x === dxmlAttachment);
        if (index >= 0) {
            const attachmentToDelete = _.cloneDeep(editorAttachment.Attachments[index]);
            attachmentToDelete.Action = Constants.attachmentActionType.delete;
            attachmentsToDeleteRef.current.push(attachmentToDelete);

            const clonedEditorAttachment = _.cloneDeep(editorAttachment);
            for (let i = index + 1; i < clonedEditorAttachment.Attachments.length; i++) {
                clonedEditorAttachment.Attachments[i].Order = clonedEditorAttachment.Attachments[i].Order - 1;
                clonedEditorAttachment.Attachments[i].Action = Constants.attachmentActionType.createOrUpdate;
            }
            clonedEditorAttachment.Attachments.splice(index, 1);

            handleSave(clonedEditorAttachment);
        }
    }

    if (libraryOpen) {
        return <AttachmentLibrary onClose={onLibraryClose} />
    }

    return (
        <div className="sm:tw-min-h-full print:tw-my-0 print:tw-min-h-screen tw-bg-brand-background tw-overflow-y-auto tw-h-full">

            <div className="tw-h-full tw-max-w-screen-2xl tw-mx-auto tw-inset-0 tw-py-6 ">
                <div className="tw-h-full tw-bg-white tw-shadow-xl tw-rounded-md">
                    <div className="tw-px-6 sm:tw-px-10 tw-py-3 tw-h-full tw-flex tw-flex-col">
                        <div className="tw-mt-3 tw-pb-4 tw-border-b tw-border-gray-200 tw-grid tw-justify-items-center ">
                            <div className="tw-flex tw-items-center tw-gap-x-3">
                                <button
                                    type="button"
                                    className="tw-grid tw-justify-items-center tw-h-14 tw-w-32 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed"
                                    onClick={handleAddFromLibrary}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleAddFromLibrary();
                                        }
                                    }}
                                    disabled={editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft}
                                >
                                    <svg
                                        tabIndex={-1}
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={classNames(editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft
                                            ?
                                            "tw-border-none tw-bg-gray-200 tw-text-gray-400 tw-cursor-not-allowed focus:tw-outline-none"
                                            :
                                            "tw-shadow-sm tw-border tw-border-gray-300 tw-text-gray-500 tw-bg-white tw-cursor-pointer hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-indigo-700 focus:tw-ring-2",
                                            "tw-h-10 tw-w-10 tw-items-center tw-px-2 tw-py-1 tw-leading-4 tw-font-medium tw-rounded-md tw-text-sm ")}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        disabled={editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft}
                                    >
                                        <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                                    </svg>
                                    <span className="tw-mt-1 tw-text-xs tw-text-center tw-text-gray-600 ">{ta(translations.attachment.addFromLibrary)}</span>
                                </button>

                                <label
                                    htmlFor="pdfUploadfile"
                                    className="tw-relative tw-grid tw-justify-items-center tw-h-14 tw-w-32 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed focus-within:tw-ring-2 focus-within:tw-ring-indigo-700 focus-within:tw-ring-offset-2"
                                    disabled={editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft || !editorFileRef.current?.WebEditorSettings?.PdfAttachmentEnabled}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={classNames(editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft || !editorFileRef.current?.WebEditorSettings?.PdfAttachmentEnabled
                                            ?
                                            "tw-border-none tw-bg-gray-200 tw-text-gray-400 tw-cursor-not-allowed focus:tw-outline-none"
                                            :
                                            "tw-shadow-sm tw-border tw-border-gray-300 tw-text-gray-500 tw-bg-white tw-cursor-pointer hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-indigo-700 focus:tw-ring-2",
                                            "tw-h-10 tw-w-10 tw-items-center tw-px-2 tw-py-1 tw-leading-4 tw-font-medium tw-rounded-md tw-text-sm ")}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span className="tw-mt-1 tw-text-xs tw-text-center tw-text-gray-600 ">{ta(translations.attachment.uploadFile)}</span>
                                    <input type="file" className='tw-sr-only' name="pdfUploadfile" id="pdfUploadfile" accept={fileExtension}
                                        onChange={(e) => {
                                            handlePdfUpload(e);
                                            e.target.value = null;
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                e.target.click();
                                            }
                                        }}
                                        disabled={editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft || !editorFileRef.current?.WebEditorSettings?.PdfAttachmentEnabled}
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="tw-shadow-sm tw-overflow-x-auto tw-overflow-y-auto tw-border-b tw-border-gray-200 sm:tw-rounded-lg tw-my-5">
                            <table className="tw-min-w-full tw-h-full tw-divide-y tw-divide-gray-200">
                                <thead className="tw-bg-gray-50">
                                    <tr>
                                        <th scope="col" className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">{ta(translations.attachment.name)}</th>
                                        <th scope="col" className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">{ta(translations.attachment.description)}</th>
                                        <th scope="col" className="tw-relative tw-px-6 tw-py-3">
                                            <span className="tw-sr-only">{ta(translations.attachment.actions)}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
                                    {
                                        editorAttachment && editorAttachment.Attachments && editorAttachment.Attachments.map((dxmlAttachment, index) => (
                                            <tr key={dxmlAttachment.Id} className="@trCssClass">
                                                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-gray-900">{dxmlAttachment.Name}</td>
                                                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">{dxmlAttachment.Description}</td>
                                                <td className="tw-relative tw-w-48 tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-right tw-text-sm tw-font-medium tw-text-gray-500">
                                                    <div className="tw-flex ">
                                                        <button
                                                            aria-label={ta(translations.attachment.moveDown)}
                                                            type="button"
                                                            className="tw--my-2 tw-p-1 tw-bg-white tw-flex tw-items-center hover:tw-text-indigo-700 disabled:tw-bg-gray-200 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed tw-border tw-border-gray-300 tw-rounded-md"
                                                            onClick={() => handleMoveDown(dxmlAttachment)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    handleMoveDown(dxmlAttachment);
                                                                }
                                                            }}
                                                            disabled={index === (editorAttachment?.Attachments?.length - 1) || editorDisabled}
                                                        >
                                                            <ArrowLongDownIcon className="tw-h-5 tw-w-6"></ArrowLongDownIcon>
                                                        </button>
                                                        <button
                                                            aria-label={ta(translations.attachment.moveUp)}
                                                            type="button"
                                                            className="tw--my-2 tw-ml-1 tw-mr-3 tw-p-1 tw-bg-white tw-flex tw-items-center hover:tw-text-indigo-700 disabled:tw-bg-gray-200 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed tw-border tw-border-gray-300 tw-rounded-md"
                                                            onClick={() => handleMoveUp(dxmlAttachment)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    handleMoveUp(dxmlAttachment);
                                                                }
                                                            }}
                                                            disabled={index === 0 || editorDisabled}
                                                        >
                                                            <ArrowLongUpIcon className="tw-h-5 tw-w-6"></ArrowLongUpIcon>
                                                        </button>
                                                        <button
                                                            aria-label={ta(translations.attachment.delete)}
                                                            type="button"
                                                            className="tw--my-2 tw-p-1 tw-bg-white tw-flex tw-items-center hover:tw-text-indigo-700 disabled:tw-bg-gray-200 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed tw-border tw-border-gray-300 tw-rounded-md"
                                                            onClick={() => handleDelete(dxmlAttachment)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    handleDelete(dxmlAttachment);
                                                                }
                                                            }}
                                                            disabled={editorDisabled}
                                                        >
                                                            <TrashIcon className="tw-h-5 tw-w-6"></TrashIcon>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    }

                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}